/**
 * Pilot-account-klient för Lyktan (S-04-08, utvidgad i konsolidering).
 *
 * Wrappar `/api/admin/pilot-accounts`-endpointerna:
 *  - POST   `/api/admin/pilot-accounts`        — skapa pilotkonto + kod (S-03-02, PR #53).
 *  - GET    `/api/admin/pilot-accounts`        — lista aktiva pilotkonton (S-04-08b).
 *  - DELETE `/api/admin/pilot-accounts/{id}`   — soft-deaktivera pilotkonto (S-04-08c).
 *
 * Engångskoden returneras EN gång — vyn ansvarar för att visa den för
 * admin och varna om säker kanal innan den lämnar webbläsaren.
 *
 * Hard-rules från CLAUDE.md:
 * - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body
 *   eller localStorage/sessionStorage.
 * - CSRF-header på muterande anrop (POST/DELETE). GET är inte CSRF-skyddad
 *   på backend (SameSite=Strict räcker för GET).
 * - Inga råa databas-ID:n exponeras — backend returnerar UUID/opaque
 *   identifierare (`user_id`, `pilot_id`, `activation_code`).
 * - Inga fritextfält skickas i request — koden + pilot-id genereras
 *   serverside.
 * - Felmeddelanden mappas till svenska användartexter; backend-felkoderna
 *   är opaka tokens.
 */

import { csrfHeader } from '../auth/csrf';

export type CreatePilotAccountSuccess = {
  kind: 'created';
  /** Opaque user-UUID från backend. Ej rå databas-ID. */
  userId: string;
  /** Serverside-genererat pilot-id (Crockford-likt, ej PII). */
  pilotId: string;
  /** Engångs-aktiveringskod. Visas EN gång och måste skickas via säker kanal. */
  activationCode: string;
  /** ISO-8601 timestamp när koden upphör (24h efter skapandet). */
  expiresAt: string;
};

export type CreatePilotAccountFailure = {
  kind: 'failure';
  message: string;
};

export type CreatePilotAccountResult = CreatePilotAccountSuccess | CreatePilotAccountFailure;

/**
 * Statusvärden som backend returnerar i list-endpointen. `pending` =
 * inbjuden men ej aktiverad, `activated` = har loggat in minst en gång,
 * `expired` = engångskoden löpte ut innan första login. `unknown`
 * fungerar som forward-compat-fallback om backend lägger till nya
 * statusvärden.
 */
export type PilotAccountStatus = 'pending' | 'activated' | 'expired' | 'unknown';

/**
 * Ett pilotkonto i list-svaret från GET /api/admin/pilot-accounts.
 * Endast opaque IDs och kategoriska fält — inga e-postadresser,
 * inga fritextfält.
 */
export type PilotAccount = {
  /** Opaque user-UUID. Råa databas-ID:n läcker aldrig. */
  userId: string;
  /** Serverside-genererat pilot-id (Crockford-likt, ej PII). */
  pilotId: string;
  /** Härledd status från backend. */
  status: PilotAccountStatus;
  /** ISO-8601 timestamp för när kontot skapades. */
  joinedAt: string;
};

export type PilotAccountListResult =
  | { kind: 'ok'; items: PilotAccount[] }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type DeactivatePilotAccountResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

const API_PATH = '/api/admin/pilot-accounts';

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera pilotkonton.';
    case 'invalid_request':
      return 'Ogiltig begäran. Försök igen.';
    case 'not_found':
      return 'Pilotkontot kunde inte hittas eller är redan inaktivt.';
    case 'contact_invalid':
      return 'Kontaktuppgifterna är ogiltiga. Kontrollera namn, mobil och e-post.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

/**
 * Valfri kontakt-info som kan följa med vid kontoskapande (S-02-12 +
 * S-04-31). `name` är obligatoriskt om objektet skickas; `phone`/`email`
 * är valfria. Tom sträng på phone/email tolkas som null på backend.
 *
 * Datakategori: "Kontaktuppgifter (admin-only)". Mobilappen har INGEN
 * åtkomst — endast Lyktan via admin-MFA-skyddade endpoints.
 */
export type CreatePilotAccountContact = {
  name: string;
  phone?: string;
  email?: string;
};

/**
 * Skapar ett nytt pilotkonto. Backend genererar pilot-id + engångskod.
 * Anroparen måste ha en aktiv admin-session (cookie); 401 mappas till
 * "Sessionen har gått ut".
 *
 * Body är medvetet en tom JSON-objektliteral — backend tillåter (och
 * kräver) det enligt PilotAccountRoutes.kt. Om kallaren skickar
 * `contact` skapas en kontakt-rad atomärt med kontot.
 */
export async function createPilotAccount(
  contact?: CreatePilotAccountContact,
): Promise<CreatePilotAccountResult> {
  // Backend förväntar `contact: {name, phone?, email?}`. Vi serialiserar
  // bara om kallaren skickar in objektet — annars tom body som tidigare.
  const requestBody: Record<string, unknown> = {};
  if (contact) {
    const trimmedName = contact.name.trim();
    if (trimmedName.length === 0) {
      return { kind: 'failure', message: 'Namn krävs.' };
    }
    requestBody.contact = {
      name: trimmedName,
      // Tom sträng → null så backend rensar fältet konsekvent
      phone: contact.phone && contact.phone.trim().length > 0 ? contact.phone.trim() : null,
      email: contact.email && contact.email.trim().length > 0 ? contact.email.trim() : null,
    };
  }

  let response: Response;
  try {
    response = await fetch(API_PATH, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify(requestBody),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401) {
    return { kind: 'failure', message: messageForError('unauthorized') };
  }
  if (response.status === 403) {
    return { kind: 'failure', message: messageForError('forbidden') };
  }

  if (response.ok) {
    const responseBody = (await response.json()) as {
      user_id?: string;
      pilot_id?: string;
      activation_code?: string;
      expires_at?: string;
    };
    if (
      typeof responseBody.user_id === 'string' &&
      typeof responseBody.pilot_id === 'string' &&
      typeof responseBody.activation_code === 'string' &&
      typeof responseBody.expires_at === 'string'
    ) {
      return {
        kind: 'created',
        userId: responseBody.user_id,
        pilotId: responseBody.pilot_id,
        activationCode: responseBody.activation_code,
        expiresAt: responseBody.expires_at,
      };
    }
    return { kind: 'failure', message: messageForError('default') };
  }

  const errorBody = await safeJson(response);
  return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
}

type RawPilotAccount = {
  user_id?: unknown;
  pilot_id?: unknown;
  status?: unknown;
  joined_at?: unknown;
};

function parsePilotAccount(raw: RawPilotAccount): PilotAccount | null {
  if (
    typeof raw.user_id !== 'string' ||
    typeof raw.pilot_id !== 'string' ||
    typeof raw.status !== 'string' ||
    typeof raw.joined_at !== 'string'
  ) {
    return null;
  }
  const status: PilotAccountStatus =
    raw.status === 'pending' || raw.status === 'activated' || raw.status === 'expired'
      ? raw.status
      : 'unknown';
  return {
    userId: raw.user_id,
    pilotId: raw.pilot_id,
    status,
    joinedAt: raw.joined_at,
  };
}

/**
 * GET /api/admin/pilot-accounts — listar pilotkonton som inte är
 * soft-deletade. Returnerar opaque IDs + status + joinedAt; ingen e-post,
 * inga fritextfält.
 */
export async function listPilotAccounts(): Promise<PilotAccountListResult> {
  let response: Response;
  try {
    response = await fetch(API_PATH, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  // Backend returnerar en JSON-array direkt (inte ett { items: [...] }-
  // wrapper-objekt) — se PilotAccountRoutes.kt list-endpointen.
  if (!Array.isArray(raw)) {
    return { kind: 'failure', message: messageForError('default') };
  }

  const items: PilotAccount[] = [];
  for (const entry of raw) {
    const parsed = parsePilotAccount(entry as RawPilotAccount);
    if (!parsed) {
      return { kind: 'failure', message: messageForError('default') };
    }
    items.push(parsed);
  }
  return { kind: 'ok', items };
}

/**
 * DELETE /api/admin/pilot-accounts/{id} — soft-deaktiverar pilotkonto.
 *
 * Backend skriver audit-rad i `admin_actions` atomärt med soft-delete.
 * Klienten får aldrig 200 — bara 204 (ok) eller 404 (not_found / okänt
 * UUID); 401/403 betyder att sessionen är ogiltig.
 *
 * Pilotkontona kräver INTE MFA-step-up (till skillnad från admin-
 * deaktivering) — backend-routen gör soft-delete utan extra MFA-grind.
 * Cookie-auth + CSRF-header räcker.
 */
export async function deactivatePilotAccount(
  userId: string,
): Promise<DeactivatePilotAccountResult> {
  let response: Response;
  try {
    response = await fetch(`${API_PATH}/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 204) {
    return { kind: 'ok' };
  }
  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }
  return { kind: 'ok' };
}
